/**
 * Shared dialogs for the configuration board (moved from WSRADM-0027 DimensionBoard).
 * AllocateByCategoryDialog is kept for WSRADM-0033.
 */
import { useEffect, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
} from '@mui/material';
import type { BoardFamily } from '@/api/dimensions/types';
import { useCopy } from '@/hooks/useCopy';

export function UnsavedChangesDialog({
  open,
  onStay,
  onLeave,
}: {
  open: boolean;
  onStay: () => void;
  onLeave: () => void;
}) {
  const { t } = useCopy();
  return (
    <Dialog open={open} onClose={onStay}>
      <DialogTitle>{t('common.unsavedChanges', 'You have unsaved changes.')}</DialogTitle>
      <DialogActions>
        <Button onClick={onStay}>{t('common.stay', 'Stay')}</Button>
        <Button onClick={onLeave}>{t('common.leave', 'Leave')}</Button>
      </DialogActions>
    </Dialog>
  );
}

export function AllocateByCategoryDialog({
  open,
  categories,
  families,
  onClose,
  onApply,
}: {
  open: boolean;
  categories: string[];
  families: BoardFamily[];
  onClose: () => void;
  onApply: (category: string, familySlug: string) => void;
}) {
  const { t } = useCopy();
  const [category, setCategory] = useState('');
  const [family, setFamily] = useState('');
  useEffect(() => {
    if (open) {
      setCategory('');
      setFamily('');
    }
  }, [open]);
  const categoryLabel = t('config.board.category', 'Category');
  const familyLabel = t('config.board.family', 'Family');
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>
        {t('config.board.allocateByCategory', 'Allocate by WStudio category')}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1, minWidth: 280 }}>
          <FormControl fullWidth>
            <InputLabel id="board-category-label">{categoryLabel}</InputLabel>
            <Select
              labelId="board-category-label"
              label={categoryLabel}
              value={category}
              onChange={(event) => setCategory(String(event.target.value))}
            >
              {categories.map((entry) => (
                <MenuItem key={entry} value={entry}>
                  {entry}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth>
            <InputLabel id="board-family-label">{familyLabel}</InputLabel>
            <Select
              labelId="board-family-label"
              label={familyLabel}
              value={family}
              onChange={(event) => setFamily(String(event.target.value))}
            >
              {families.map((entry) => (
                <MenuItem key={entry.slug} value={entry.slug}>
                  {entry.displayName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
        <Button
          onClick={() => onApply(category, family)}
          disabled={category === '' || family === ''}
        >
          {t('config.board.apply', 'Apply')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
